import { createStore as createZustandStore } from 'zustand/vanilla';
import { persist } from 'zustand/middleware';
const DEFAULT_FLAGS = {
    secretRevealedToApi: false,
    consensusPhase: 'none',
    error: null,
    manualClaimStartedAt: null,
};
const STORAGE_KEY = 'train:swaps';
const initialState = {
    swaps: {},
    swapFlags: {},
    orderData: {},
    swapSubscribers: {},
};
/** Helper to update a single swap flags entry immutably */
function updateFlags(state, hashlock, updater) {
    const flags = state.swapFlags[hashlock];
    if (!flags)
        return state;
    return { swapFlags: { ...state.swapFlags, [hashlock]: updater(flags) } };
}
function createActions(set, get) {
    return {
        // --- Persisted swap actions ---
        addSwap: (hashlock, data) => set((state) => ({
            swaps: { ...state.swaps, [hashlock]: { ...data, hashlock, createdAt: Date.now() } },
            swapFlags: { ...state.swapFlags, [hashlock]: state.swapFlags[hashlock] ?? { ...DEFAULT_FLAGS } },
        })),
        updateSwap: (hashlock, updates) => set((state) => {
            if (!state.swaps[hashlock])
                return state;
            return {
                swaps: { ...state.swaps, [hashlock]: { ...state.swaps[hashlock], ...updates } },
            };
        }),
        clearSwap: (hashlock) => set((state) => {
            const { [hashlock]: _, ...rest } = state.swaps;
            return { swaps: rest };
        }),
        findSwapByTx: (sourceNetwork, txHash) => {
            const { swaps } = get();
            const upperNetwork = sourceNetwork.toUpperCase();
            const upperTxHash = txHash.toUpperCase();
            const entry = Object.entries(swaps).find(([, swap]) => swap.source?.toUpperCase() === upperNetwork &&
                swap.txId?.toUpperCase() === upperTxHash);
            return entry ? [entry[0], entry[1]] : null;
        },
        // --- Subscriber actions ---
        subscribe: (hashlock) => set((state) => {
            const count = (state.swapSubscribers[hashlock] ?? 0) + 1;
            const updates = {
                swapSubscribers: { ...state.swapSubscribers, [hashlock]: count },
            };
            // First subscriber — initialize flags from persisted data if not already present
            if (count === 1 && !state.swapFlags[hashlock] && state.swaps[hashlock]) {
                const swap = state.swaps[hashlock];
                updates.swapFlags = {
                    ...state.swapFlags,
                    [hashlock]: {
                        ...DEFAULT_FLAGS,
                        secretRevealedToApi: swap.secretRevealed ?? false,
                    },
                };
            }
            return updates;
        }),
        unsubscribe: (hashlock) => set((state) => {
            const count = (state.swapSubscribers[hashlock] ?? 1) - 1;
            if (count > 0) {
                return { swapSubscribers: { ...state.swapSubscribers, [hashlock]: count } };
            }
            // Last subscriber — clean up all ephemeral state
            const { [hashlock]: _s, ...restSubs } = state.swapSubscribers;
            const { [hashlock]: _f, ...restFlags } = state.swapFlags;
            const { [hashlock]: _o, ...restOrders } = state.orderData;
            return {
                swapSubscribers: restSubs,
                swapFlags: restFlags,
                orderData: restOrders,
            };
        }),
        // --- Flag actions ---
        updateSwapFlags: (hashlock, updates) => set((state) => updateFlags(state, hashlock, (flags) => {
            // manualClaimStartedAt is write-once
            if (updates.manualClaimStartedAt && flags.manualClaimStartedAt) {
                const { manualClaimStartedAt: _, ...rest } = updates;
                return { ...flags, ...rest };
            }
            return { ...flags, ...updates };
        })),
        // --- Order data ---
        setOrderData: (hashlock, data) => set((state) => ({
            orderData: { ...state.orderData, [hashlock]: data },
        })),
        // --- Convenience accessors ---
        getSwap: (hashlock) => get().swaps[hashlock],
        getSwapFlags: (hashlock) => get().swapFlags[hashlock],
        getOrderData: (hashlock) => get().orderData[hashlock],
    };
}
function createPersistStorage(storage) {
    if (typeof window === 'undefined' && !storage)
        return undefined;
    const underlying = storage ?? (typeof window !== 'undefined' ? window.localStorage : undefined);
    if (!underlying)
        return undefined;
    return {
        getItem: (name) => {
            const raw = underlying.getItem(name);
            if (raw instanceof Promise) {
                return raw.then(v => {
                    if (!v)
                        return null;
                    try {
                        return JSON.parse(v);
                    }
                    catch {
                        return null;
                    }
                });
            }
            if (!raw)
                return null;
            try {
                return JSON.parse(raw);
            }
            catch {
                return null;
            }
        },
        setItem: (name, value) => {
            underlying.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
            underlying.removeItem(name);
        },
    };
}
export function createSwapStore(options) {
    const shouldPersist = options?.persist !== false;
    if (!shouldPersist) {
        return createZustandStore()((set, get) => ({
            ...initialState,
            ...createActions(set, get),
        }));
    }
    const persistStorage = createPersistStorage(options?.storage);
    return createZustandStore()(persist((set, get) => ({
        ...initialState,
        ...createActions(set, get),
    }), {
        name: STORAGE_KEY,
        storage: persistStorage,
        partialize: (state) => ({
            swaps: state.swaps,
        }),
    }));
}
